/**
 * Ask AI — 백엔드 `app/assistant/router.py` 와 같은 모양.
 *
 * 답은 실제 모델을 부르지 않고 **지어낸다.** 목은 화면을 돌려 보기 위한 것이고, 여기서 진짜
 * 모델을 부르면 목으로 켠 개발에도 요금이 붙는다. 대신 모델 목록 · 이번 달 금액 · 상한 초과는
 * 서버와 같은 규칙으로 흉내 내어, 켜졌을 때와 꺼졌을 때의 화면을 둘 다 볼 수 있게 한다.
 */
import { HttpResponse, http } from "msw";
import type { AssistantConfig, AssistantMessage, AssistantSendInput } from "@/types";
import { db } from "../db";
import { fail, latency, requireUser } from "../http";

const BASE = "/api/v1";

/** 백엔드 `app/assistant/service.py` 의 MODELS 와 같은 셋. */
const MODELS = [
  { id: "claude-opus-5", label: "Claude Opus 5", price: 60 },
  { id: "claude-sonnet-5", label: "Claude Sonnet 5", price: 12 },
  { id: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5", price: 4 },
];
const DEFAULT_MODEL = "claude-sonnet-5";
/** 한 달 상한(원). 0 이면 제한이 없다 — 서버는 AI_MONTHLY_CAP_KRW 에서 읽는다. */
const LIMIT_KRW = 50_000;

let seq = 0;
const now = () => new Date().toISOString();

const usage = () => db.assistantMessages.reduce((sum, message) => sum + message.cost_krw, 0);

const toMessage = (message: (typeof db.assistantMessages)[number]): AssistantMessage => ({
  id: message.id,
  role: message.role,
  content: message.content,
  model: message.model,
  created_at: message.created_at,
});

/** 제목은 첫 질문에서 딴다 — 서버와 같다. */
const titleOf = (content: string) => content.replace(/\s+/g, " ").trim().slice(0, 40);

/**
 * 지어낸 답. 무엇을 물어도 같은 틀로 답하되 **사무소 데이터를 모른다는 것**은 그대로 지킨다 —
 * 그 안내가 화면에 어떻게 보이는지가 목으로 확인할 값이다.
 */
function fakeAnswer(question: string): string {
  if (/프로젝트|금액|계약|연차|누가|직원/.test(question))
    return "저는 사무소 데이터를 보지 못해요. 프로젝트·금액·다른 분의 기록은 해당 화면에서 확인해 주세요.\n\n일반적인 방법이나 문서 쓰는 요령은 도와드릴 수 있어요.";
  return `목(mock) 응답이에요. 실제 모델을 부르지 않습니다.\n\n물어보신 것: ${titleOf(question)}\n\n여기에 모델의 답이 들어갑니다. 줄바꿈과 긴 문단이 화면에서 어떻게 보이는지 확인하려고 두 문단으로 둡니다.`;
}

export const assistantHandlers = [
  http.get(`${BASE}/assistant/config/`, async () => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const config: AssistantConfig = {
      enabled: true,
      models: MODELS.map(({ id, label }) => ({ id, label })),
      default_model: DEFAULT_MODEL,
      spent_krw: usage(),
      limit_krw: LIMIT_KRW,
    };
    return HttpResponse.json(config);
  }),

  http.get(`${BASE}/assistant/conversations/`, async () => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const results = db.assistantConversations
      .filter((row) => row.user_id === user.id)
      .sort((a, b) => b.updated_at.localeCompare(a.updated_at))
      .map(({ user_id: _, ...row }) => row);
    return HttpResponse.json({ results });
  }),

  http.get(`${BASE}/assistant/conversations/:id/`, async ({ params }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const conversation = db.assistantConversations.find(
      (row) => row.id === params.id && row.user_id === user.id
    );
    if (!conversation) return fail(404, "없는 대화입니다.");
    const results = db.assistantMessages
      .filter((message) => message.conversation_id === conversation.id)
      .sort((a, b) => a.created_at.localeCompare(b.created_at))
      .map(toMessage);
    return HttpResponse.json({ results });
  }),

  http.delete(`${BASE}/assistant/conversations/:id/`, async ({ params }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const index = db.assistantConversations.findIndex(
      (row) => row.id === params.id && row.user_id === user.id
    );
    if (index < 0) return fail(404, "없는 대화입니다.");
    db.assistantConversations.splice(index, 1);
    // 메시지도 같이 지운다 — 서버는 대화에 매달린 행을 함께 지운다.
    for (let i = db.assistantMessages.length - 1; i >= 0; i--)
      if (db.assistantMessages[i].conversation_id === params.id) db.assistantMessages.splice(i, 1);
    return new HttpResponse(null, { status: 204 });
  }),

  http.post(`${BASE}/assistant/messages/`, async ({ request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const input = (await request.json()) as AssistantSendInput;
    if (!input.content.trim()) return fail(422, "물어볼 내용을 적어 주세요.");
    if (!MODELS.some((model) => model.id === input.model)) return fail(422, "없는 모델입니다.");
    if (LIMIT_KRW > 0 && usage() >= LIMIT_KRW)
      return fail(409, "이번 달 AI 사용 한도를 다 썼어요.");

    let conversation = db.assistantConversations.find(
      (row) => row.id === input.conversation_id && row.user_id === user.id
    );
    if (!conversation) {
      conversation = {
        id: `conv-new-${++seq}`,
        user_id: user.id,
        title: titleOf(input.content),
        created_at: now(),
        updated_at: now(),
      };
      db.assistantConversations.push(conversation);
    }
    conversation.updated_at = now();

    const price = MODELS.find((model) => model.id === input.model)!.price;
    const question = {
      id: `msg-new-${++seq}`,
      conversation_id: conversation.id,
      role: "user" as const,
      content: input.content,
      model: input.model,
      cost_krw: 0,
      created_at: now(),
    };
    const answer = {
      id: `msg-new-${++seq}`,
      conversation_id: conversation.id,
      role: "assistant" as const,
      content: fakeAnswer(input.content),
      model: input.model,
      // 글자 수로 대충 매긴다 — 금액이 올라가는 것만 화면에서 보이면 된다.
      cost_krw: price + Math.round(input.content.length / 10),
      created_at: now(),
    };
    db.assistantMessages.push(question, answer);

    return HttpResponse.json(
      {
        conversation_id: conversation.id,
        title: conversation.title,
        question: toMessage(question),
        answer: toMessage(answer),
        spent_krw: usage(),
        limit_krw: LIMIT_KRW,
      },
      { status: 201 }
    );
  }),
];
